import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ingredientInventoryService from '../services/ingredientInventoryService';
import ingredientService from '../services/ingredientService';
import supplierService from '../services/supplierService';
import SupplierPickerField from '../components/SupplierPickerField';
import ReceiptScanReviewModal from '../components/ReceiptScanReviewModal';
import PushReceiptToQboModal from '../components/PushReceiptToQboModal';
import IngredientPhysicalCountModal from '../components/IngredientPhysicalCountModal';
import SortableHeader from '../components/SortableHeader';
import SearchableSelect from '../components/SearchableSelect';
import { sortRows, makeSortHandler } from '../utils/sortRows';
import { usePermissions } from '../context/PermissionContext';

// Local calendar date as "YYYY-MM-DD" — deliberately NOT `toISOString().split('T')[0]`, which
// converts to the UTC calendar date and silently shows "tomorrow" for part of the evening in any
// US (UTC-negative) timezone.
const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => toLocalDateStr(new Date());
// Mirrors the backend's isPlausibleDeliveryDate bound — a couple days of future slack covers
// timezone edge cases on same-day entry, not a loophole.
const maxDeliveryDate = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return toLocalDateStr(d);
})();
// A plain "YYYY-MM-DD" (DATEONLY) string handed to `new Date(str)` is parsed as UTC midnight —
// displaying it via `.toLocaleDateString()`/`.toLocaleString()` in any US timezone then renders
// one calendar day EARLIER than the date actually stored (the reported "logged as yesterday" bug).
// Parsing the components into a local Date instead avoids the UTC round-trip entirely.
const parseDateOnly = (d) => {
  if (!d) return null;
  const [y, m, day] = String(d).split('-').map(Number);
  return new Date(y, m - 1, day);
};

function stockColorClass(stockKg) {
  if (stockKg <= 0) return 'text-danger fw-bold';
  if (stockKg < 10) return 'text-warning fw-bold';
  return 'text-success';
}

function docTypeLabel(type) {
  if (!type) return '';
  const map = { bol: 'BOL', receipt: 'Receipt', prior_notice: 'Prior Notice', other: 'Other' };
  return map[type] || type;
}

const LB_TO_KG = 0.453592;

const getLoggedInUsername = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return '';
    const decoded = JSON.parse(atob(token.split('.')[1]));
    return decoded?.name || decoded?.username || '';
  } catch {
    return '';
  }
};

// ── Reusable confirm/alert modal ─────────────────────────────────────────────
function ConfirmModal({ dialog, onResolve }) {
  if (!dialog) return null;
  const isAlert = dialog.type === 'alert';
  const variant = dialog.variant || (isAlert ? 'primary' : 'danger');
  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content shadow">
          <div className="modal-header border-0 pb-1">
            <h5 className="modal-title fw-semibold">{dialog.title}</h5>
          </div>
          <div className="modal-body pt-1 pb-3">
            {dialog.message.split('\n\n').map((para, i) => (
              <p key={i} className={`text-muted${i === dialog.message.split('\n\n').length - 1 ? ' mb-0' : ''}`}>{para}</p>
            ))}
          </div>
          <div className="modal-footer border-0 pt-0">
            {!isAlert && (
              <button className="btn btn-outline-secondary" onClick={() => onResolve(false)}>
                Cancel
              </button>
            )}
            <button className={`btn btn-${variant}`} onClick={() => onResolve(true)}>
              {dialog.confirmLabel || (isAlert ? 'OK' : 'Confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Returns [dialogState, showConfirm, showAlert, resolveDialog]
function useDialog() {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const showConfirm = useCallback((title, message, opts = {}) => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setDialog({ type: 'confirm', title, message, ...opts });
    });
  }, []);

  const showAlert = useCallback((message, title = 'Error') => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setDialog({ type: 'alert', title, message });
    });
  }, []);

  const onResolve = useCallback((value) => {
    setDialog(null);
    resolveRef.current?.(value);
  }, []);

  return [dialog, showConfirm, showAlert, onResolve];
}

function evalMathExpr(str) {
  if (str === '' || str == null) return null;
  const clean = String(str).replace(/[xX]/g, '*').replace(/\s/g, '');
  if (!/^[\d+\-*/().]+$/.test(clean)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function('"use strict"; return (' + clean + ')')();
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
}

function MathInput({ value, onChange, onCommit, className, placeholder, style, disabled }) {
  const commit = (raw) => {
    if (raw === '' || raw == null) return;
    const result = evalMathExpr(raw);
    if (result !== null) onCommit(String(parseFloat(result.toFixed(6))));
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      style={style}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(value); e.target.blur(); } }}
      onBlur={() => commit(value)}
    />
  );
}

function IngredientSearch({ ingredients, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef();

  const selected = ingredients.find(ing => String(ing.id) === String(value));

  // When a value is selected externally (e.g. preselected), sync query
  useEffect(() => {
    if (selected) {
      setQuery(selected.name + (selected.rmCode ? ` (${selected.rmCode})` : ''));
    } else {
      setQuery('');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!query.trim()) return ingredients;
    const q = query.toLowerCase();
    return ingredients.filter(ing =>
      ing.name.toLowerCase().includes(q) ||
      (ing.rmCode && ing.rmCode.toLowerCase().includes(q))
    );
  }, [query, ingredients]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        // Restore display text to selected value on blur-away
        if (selected) {
          setQuery(selected.name + (selected.rmCode ? ` (${selected.rmCode})` : ''));
        } else {
          setQuery('');
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selected]);

  const select = (ing) => {
    onChange(String(ing.id));
    setQuery(ing.name + (ing.rmCode ? ` (${ing.rmCode})` : ''));
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder="Search by name or RM code…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); onChange(''); }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {value && (
          <button type="button" className="btn btn-outline-secondary" onClick={clear} tabIndex={-1}>
            &times;
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul
          className="list-group shadow-sm"
          style={{
            position: 'absolute', zIndex: 1055, width: '100%',
            maxHeight: 220, overflowY: 'auto',
            border: '1px solid #dee2e6', borderRadius: '0 0 4px 4px',
            marginTop: 1,
          }}
        >
          {filtered.map(ing => (
            <li
              key={ing.id}
              className="list-group-item list-group-item-action py-1 px-2"
              style={{ cursor: 'pointer' }}
              onMouseDown={e => { e.preventDefault(); select(ing); }}
            >
              {ing.name}
              {ing.rmCode && <span className="ms-2 badge bg-secondary">{ing.rmCode}</span>}
            </li>
          ))}
        </ul>
      )}
      {open && query.trim() && filtered.length === 0 && (
        <div
          className="border rounded p-2 text-muted small bg-white shadow-sm"
          style={{ position: 'absolute', zIndex: 1055, width: '100%', marginTop: 1 }}
        >
          No ingredients found. Add it in Products &amp; Ingredients first.
        </div>
      )}
    </div>
  );
}

function buildEmptyForm(_, preselectedIngredientId) {
  return {
    ingredientId: preselectedIngredientId || '',
    deliveryDate: today(),
    supplierId: '',
    unitCost: '',
    receivedByUserId: '',
    receivedByName: getLoggedInUsername(),
    isTemperatureControlled: false,
    receivedTemperature: '',
    tempUnit: 'C',
    isFrozen: false,
    checkFrozenIsFrozen: false,
    checkFrozenNoThawing: false,
    hasRejection: false,
    rejectionDescription: '',
    rejectedQuantityKg: '',
    deviation: '',
    correctiveAction: '',
    verifiedByUserId: '',
    verifiedByName: '',
    verifiedAt: '',
    documentType: '',
    notes: '',
    lotCodes: [{ lotCode: '', quantityKg: '' }],
    checkResults: [],
    unit: 'kg',
  };
}

function conditionVisible(condition, form) {
  if (condition === 'always') return true;
  if (condition === 'temperatureControlled') return form.isTemperatureControlled;
  if (condition === 'frozen') return form.isFrozen;
  return true;
}

function conditionHint(condition) {
  if (condition === 'temperatureControlled') return '(shown because temperature controlled)';
  if (condition === 'frozen') return '(shown because frozen)';
  return '';
}

function IngredientInventoryPage() {
  const { role, companySettings } = usePermissions();
  const [tab, setTab] = useState('stock');

  const [stock, setStock] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  const defaultStockRange = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    const fmt = d => d.toISOString().slice(0, 10);
    return { usedFrom: fmt(from), usedTo: fmt(to), preset: '30d' };
  };
  const [stockRange, setStockRange] = useState(() => defaultStockRange());

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [filters, setFilters] = useState({ search: '', ingredientId: '', dateFrom: '', dateTo: '' });
  const [appliedFilters, setAppliedFilters] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [form, setForm] = useState(() => buildEmptyForm(null, ''));
  const [checkTemplates, setCheckTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [pendingDocs, setPendingDocs] = useState([]); // [{ file, documentType }] — queued for upload on save
  const [savedDocuments, setSavedDocuments] = useState([]); // existing docs from server for edit modal
  const flushDocRef = useRef(null); // set by DocumentsSection to flush any staged-but-not-added doc

  const [adjustments, setAdjustments] = useState([]);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);

  const [physicalCounts, setPhysicalCounts] = useState([]);
  const [physicalCountsLoading, setPhysicalCountsLoading] = useState(false);
  const [showPhysicalCountModal, setShowPhysicalCountModal] = useState(false);
  const [expandedCountId, setExpandedCountId] = useState(null);
  const [expandedCountDetail, setExpandedCountDetail] = useState(null);
  const [showAdjModal, setShowAdjModal] = useState(false);
  const [adjForm, setAdjForm] = useState({ ingredientId: '', lotCode: '', quantityKg: '', adjustmentType: 'waste', reason: '', notes: '' });
  const [adjSaving, setAdjSaving] = useState(false);
  const [adjError, setAdjError] = useState('');

  const [verifyLog, setVerifyLog] = useState(null);

  const [showDocModal, setShowDocModal] = useState(false);
  const [docModalLog, setDocModalLog] = useState(null);
  const [docModalFile, setDocModalFile] = useState(null);
  const [docModalType, setDocModalType] = useState('');
  const [docModalSaving, setDocModalSaving] = useState(false);
  const [docModalError, setDocModalError] = useState('');
  const docModalFileRef = useRef();

  const [scanFile, setScanFile] = useState(null);
  const scanFileRef = useRef();
  const scanCameraRef = useRef();

  const [pushQboScanId, setPushQboScanId] = useState(null);

  const isAdmin = role === 'admin' || role === 'superadmin';
  // Verifying a receiving log is a QA function, same as Baking/Prep CCP verify — admin-only was
  // an inconsistency with the rest of the app (see backend's requireQAOrAdmin), not a deliberate
  // restriction.
  const canVerifyReceiving = isAdmin || role === 'qa';

  const [showCheckConfig, setShowCheckConfig] = useState(false);
  const [printLog, setPrintLog] = useState(null);
  const [priceHistoryPrompt, setPriceHistoryPrompt] = useState(null); // { ingredientId, ingredientName, pricePerKg, deliveryDate }
  const [dialog, showConfirm, showAlert, onResolveDialog] = useDialog();

  const fetchStock = useCallback(async (range) => {
    setStockLoading(true);
    try {
      const r = range || stockRange;
      const params = {};
      if (r.usedFrom) params.usedFrom = r.usedFrom;
      if (r.usedTo) params.usedTo = r.usedTo;
      const data = await ingredientInventoryService.getStock(params);
      setStock(Array.isArray(data) ? data : []);
    } catch {
      setStock([]);
    } finally {
      setStockLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = useCallback(async (f = {}) => {
    setLogsLoading(true);
    try {
      const params = {};
      if (f.search) params.search = f.search;
      if (f.ingredientId) params.ingredientId = f.ingredientId;
      if (f.dateFrom) params.dateFrom = f.dateFrom;
      if (f.dateTo) params.dateTo = f.dateTo;
      const data = await ingredientInventoryService.getReceivingLogs(params);
      setLogs(Array.isArray(data) ? data : []);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const fetchIngredients = useCallback(async () => {
    try {
      const data = await ingredientService.getIngredients();
      setIngredients(Array.isArray(data) ? data : []);
    } catch {
      setIngredients([]);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const data = await supplierService.getSuppliers();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch {
      setSuppliers([]);
    }
  }, []);

  const fetchAdjustments = useCallback(async () => {
    setAdjustmentsLoading(true);
    try {
      const data = await ingredientInventoryService.getAdjustments();
      setAdjustments(Array.isArray(data) ? data : []);
    } catch {
      setAdjustments([]);
    } finally {
      setAdjustmentsLoading(false);
    }
  }, []);

  const fetchPhysicalCounts = useCallback(async () => {
    setPhysicalCountsLoading(true);
    try {
      const data = await ingredientInventoryService.getPhysicalCounts();
      setPhysicalCounts(Array.isArray(data) ? data : []);
    } catch {
      setPhysicalCounts([]);
    } finally {
      setPhysicalCountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIngredients();
    fetchSuppliers();
    fetchStock(stockRange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchIngredients, fetchSuppliers, fetchStock]);

  useEffect(() => {
    if (tab === 'receiving') {
      fetchLogs(appliedFilters);
    }
    if (tab === 'adjustments') {
      fetchAdjustments();
    }
    if (tab === 'physicalCounts') {
      fetchPhysicalCounts();
    }
  }, [tab, appliedFilters, fetchLogs, fetchAdjustments, fetchPhysicalCounts]);

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    const empty = { search: '', ingredientId: '', dateFrom: '', dateTo: '' };
    setFilters(empty);
    setAppliedFilters({});
  };

  const openNewLogModal = (preIngredientId = '') => {
    setEditingLog(null);
    setPendingDocs([]);
    setSavedDocuments([]);
    setFormError('');
    const base = buildEmptyForm(null, preIngredientId);
    setForm(base);
    loadCheckTemplatesForModal(base);
    setShowModal(true);
  };

  const openEditModal = async (log) => {
    setEditingLog(log);
    setPendingDocs([]);
    setSavedDocuments(log.Documents || []);
    setFormError('');
    const base = {
      ingredientId: log.ingredientId || '',
      deliveryDate: log.deliveryDate || today(),
      supplierId: log.supplierId != null ? String(log.supplierId) : '',
      unitCost: log.unitCost != null ? String(log.unitCost) : '',
      receivedByUserId: log.receivedByUserId || '',
      receivedByName: log.receivedByName || '',
      isTemperatureControlled: !!log.isTemperatureControlled,
      receivedTemperature: log.receivedTemperature != null ? String(log.receivedTemperature) : '',
      tempUnit: 'C',
      isFrozen: !!log.isFrozen,
      checkFrozenIsFrozen: !!log.checkFrozenIsFrozen,
      checkFrozenNoThawing: !!log.checkFrozenNoThawing,
      hasRejection: !!log.hasRejection,
      rejectionDescription: log.rejectionDescription || '',
      rejectedQuantityKg: log.rejectedQuantityKg != null ? String(log.rejectedQuantityKg) : '',
      deviation: log.deviation || '',
      correctiveAction: log.correctiveAction || '',
      verifiedByUserId: log.verifiedByUserId || '',
      verifiedByName: log.verifiedByName || '',
      verifiedAt: log.verifiedAt ? log.verifiedAt.split('T')[0] : '',
      documentType: log.documentType || '',
      notes: log.notes || '',
      unit: 'kg',
      lotCodes: (log.LotCodes && log.LotCodes.length > 0)
        ? log.LotCodes.map(lc => ({ lotCode: lc.lotCode, quantityKg: String(lc.quantityKg) }))
        : [{ lotCode: '', quantityKg: '' }],
      checkResults: (log.CheckResults || []).map(cr => ({
        checkTemplateId: cr.checkTemplateId,
        checkLabelSnapshot: cr.checkLabelSnapshot,
        passed: cr.passed !== false,
        notes: cr.notes || '',
      })),
    };
    setForm(base);
    loadCheckTemplatesForModal(base, log.CheckResults || []);
    setShowModal(true);
  };

  const loadCheckTemplatesForModal = async (baseForm, existingResults = []) => {
    try {
      const templates = await ingredientInventoryService.getCheckTemplates();
      const enabled = (Array.isArray(templates) ? templates : []).filter(t => t.enabled !== false);
      setCheckTemplates(enabled);

      if (existingResults.length > 0) {
        return;
      }

      const checkResults = enabled.map(t => ({
        checkTemplateId: t.id,
        checkLabelSnapshot: t.label,
        passed: true,
        notes: '',
      }));
      setForm(f => ({ ...f, checkResults }));
    } catch {
      setCheckTemplates([]);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLog(null);
    setPendingDocs([]);
    setSavedDocuments([]);
    setFormError('');
  };

  const handleDeleteSavedDocument = async (docId) => {
    if (!editingLog) return;
    try {
      await ingredientInventoryService.deleteDocument(editingLog.id, docId);
      setSavedDocuments(d => d.filter(doc => doc.id !== docId));
    } catch {
      // ignore
    }
  };

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const setLotCode = (index, key, value) => {
    setForm(f => {
      const lotCodes = f.lotCodes.map((lc, i) => i === index ? { ...lc, [key]: value } : lc);
      return { ...f, lotCodes };
    });
  };

  const addLotCode = () => setForm(f => ({ ...f, lotCodes: [...f.lotCodes, { lotCode: '', quantityKg: '' }] }));

  const removeLotCode = (index) => {
    setForm(f => {
      if (f.lotCodes.length <= 1) return f;
      return { ...f, lotCodes: f.lotCodes.filter((_, i) => i !== index) };
    });
  };

  const setCheckResult = (templateId, key, value) => {
    setForm(f => ({
      ...f,
      checkResults: f.checkResults.map(cr =>
        cr.checkTemplateId === templateId ? { ...cr, [key]: value } : cr
      ),
    }));
  };

  const totalQuantityKg = form.lotCodes.reduce((sum, lc) => {
    const n = parseFloat(lc.quantityKg);
    if (isNaN(n)) return sum;
    return sum + (form.unit === 'lb' ? n * LB_TO_KG : n);
  }, 0);

  const validate = () => {
    if (!form.ingredientId) return 'Ingredient is required.';
    if (!form.deliveryDate) return 'Delivery date is required.';
    if (!form.supplierId) return 'Supplier is required.';
    const validLotCodes = form.lotCodes.filter(lc => lc.lotCode.trim() && lc.quantityKg !== '');
    if (validLotCodes.length === 0) return 'At least one lot code with a quantity is required.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError('');
    setSaving(true);

    try {
      const isLb = form.unit === 'lb';
      const toKg = (v) => isLb ? parseFloat(v) * LB_TO_KG : parseFloat(v);
      const costToKg = (v) => isLb ? parseFloat(v) / LB_TO_KG : parseFloat(v);
      const toC = (v) => form.tempUnit === 'F' ? (parseFloat(v) - 32) * 5 / 9 : parseFloat(v);

      const payload = {
        ingredientId: form.ingredientId,
        deliveryDate: form.deliveryDate,
        supplierId: form.supplierId,
        unitCost: form.unitCost !== '' ? costToKg(form.unitCost) : null,
        receivedByUserId: form.receivedByUserId || null,
        receivedByName: form.receivedByName.trim() || null,
        isTemperatureControlled: form.isTemperatureControlled,
        receivedTemperature: form.isTemperatureControlled && form.receivedTemperature !== '' ? toC(form.receivedTemperature) : null,
        isFrozen: form.isTemperatureControlled ? form.isFrozen : false,
        checkFrozenIsFrozen: (form.isTemperatureControlled && form.isFrozen) ? form.checkFrozenIsFrozen : null,
        checkFrozenNoThawing: (form.isTemperatureControlled && form.isFrozen) ? form.checkFrozenNoThawing : null,
        hasRejection: form.hasRejection,
        rejectionDescription: form.hasRejection ? form.rejectionDescription.trim() || null : null,
        rejectedQuantityKg: form.hasRejection && form.rejectedQuantityKg !== '' ? toKg(form.rejectedQuantityKg) : null,
        deviation: form.deviation.trim() || null,
        correctiveAction: form.correctiveAction.trim() || null,
        notes: form.notes.trim() || null,
        lotCodes: form.lotCodes
          .filter(lc => lc.lotCode.trim() && lc.quantityKg !== '')
          .map(lc => ({ lotCode: lc.lotCode.trim(), quantityKg: toKg(lc.quantityKg) })),
        checkResults: form.checkResults
          .filter(cr => {
            const template = checkTemplates.find(t => t.id === cr.checkTemplateId);
            return template && conditionVisible(template.condition, form);
          })
          .map(cr => ({
            checkTemplateId: cr.checkTemplateId,
            checkLabelSnapshot: cr.checkLabelSnapshot,
            passed: cr.passed,
            notes: cr.notes.trim() || null,
          })),
      };

      let savedLog;
      if (editingLog) {
        savedLog = await ingredientInventoryService.updateReceivingLog(editingLog.id, payload);
      } else {
        savedLog = await ingredientInventoryService.createReceivingLog(payload);
      }

      // Upload pending documents (flush any staged-but-not-added doc first)
      const stagedDoc = flushDocRef.current?.();
      const allPendingDocs = stagedDoc ? [...pendingDocs, stagedDoc] : pendingDocs;
      const logId = savedLog?.id || editingLog?.id;
      if (logId && allPendingDocs.length > 0) {
        for (const pd of allPendingDocs) {
          try {
            const { uploadUrl, key } = await ingredientInventoryService.getDocumentUploadUrlMulti(logId, pd.file.name, pd.file.type);
            await ingredientInventoryService.uploadDocumentToS3(uploadUrl, pd.file);
            await ingredientInventoryService.saveDocument(logId, {
              documentType: pd.documentType,
              documentKey: key,
              documentOriginalName: pd.file.name,
            });
          } catch {
            // document upload failed silently — log was still saved
          }
        }
      }

      // Capture price info before closing modal (form will be cleared)
      const priceForHistory = form.unitCost !== ''
        ? {
            ingredientId: form.ingredientId,
            ingredientName: ingredients.find(i => i.id === Number(form.ingredientId))?.name || '',
            pricePerKg: payload.unitCost,
            deliveryDate: form.deliveryDate,
          }
        : null;

      closeModal();
      fetchStock();
      if (tab === 'receiving') fetchLogs(appliedFilters);

      if (priceForHistory) {
        setPriceHistoryPrompt(priceForHistory);
      }
    } catch (e) {
      setFormError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await showConfirm('Delete Receiving Log', 'Delete this receiving log? This cannot be undone.', { confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      await ingredientInventoryService.deleteReceivingLog(id);
      fetchStock();
      fetchLogs(appliedFilters);
    } catch {
      // ignore
    }
  };

  const handleOpenDocument = async (logId) => {
    try {
      const { url } = await ingredientInventoryService.getDocumentUrl(logId);
      window.open(url, '_blank');
    } catch {
      // ignore
    }
  };

  const openDocModal = (log) => {
    setDocModalLog(log);
    setDocModalFile(null);
    setDocModalType('');
    setDocModalError('');
    setShowDocModal(true);
  };

  const handleDocSave = async () => {
    if (!docModalFile || !docModalType) { setDocModalError('Select a document type and file.'); return; }
    setDocModalError('');
    setDocModalSaving(true);
    try {
      const logId = docModalLog.id;
      const { uploadUrl, key } = await ingredientInventoryService.getDocumentUploadUrlMulti(logId, docModalFile.name, docModalFile.type);
      await ingredientInventoryService.uploadDocumentToS3(uploadUrl, docModalFile);
      await ingredientInventoryService.saveDocument(logId, {
        documentType: docModalType,
        documentKey: key,
        documentOriginalName: docModalFile.name,
      });
      setShowDocModal(false);
      fetchLogs(appliedFilters);
    } catch {
      setDocModalError('Save failed. Please try again.');
    } finally {
      setDocModalSaving(false);
    }
  };

  const handleDeleteDocFromModal = async (logId, docId) => {
    try {
      await ingredientInventoryService.deleteDocument(logId, docId);
      // refresh logs so the table updates
      fetchLogs(appliedFilters);
      // update docModalLog to reflect removal
      setDocModalLog(prev => prev ? { ...prev, Documents: (prev.Documents || []).filter(d => d.id !== docId) } : prev);
    } catch {
      // ignore
    }
  };

  const openVerifyModal = async (logId) => {
    try {
      const [fullLog] = await Promise.all([
        ingredientInventoryService.getReceivingLog(logId),
        checkTemplates.length === 0
          ? ingredientInventoryService.getCheckTemplates().then(templates => {
              setCheckTemplates((Array.isArray(templates) ? templates : []).filter(t => t.enabled !== false));
            }).catch(() => setCheckTemplates([]))
          : Promise.resolve(),
      ]);
      setVerifyLog(fullLog);
    } catch {
      await showAlert('Could not load this record for verification.', 'Error');
    }
  };

  const handleVerified = () => {
    setVerifyLog(null);
    fetchLogs(appliedFilters);
  };

  const handlePrintLog = async (logId) => {
    try {
      const log = await ingredientInventoryService.getReceivingLog(logId);
      setPrintLog(log);
    } catch {
      await showAlert('Could not load record for printing.', 'Print Error');
    }
  };

  const handleCreateAdjustment = async () => {
    if (!adjForm.ingredientId) { setAdjError('Ingredient is required.'); return; }
    if (!adjForm.quantityKg || isNaN(parseFloat(adjForm.quantityKg))) { setAdjError('Quantity is required.'); return; }
    if (!adjForm.reason.trim()) { setAdjError('Reason is required.'); return; }
    setAdjError('');
    setAdjSaving(true);
    try {
      await ingredientInventoryService.createAdjustment({
        ingredientId: adjForm.ingredientId,
        lotCode: adjForm.lotCode.trim() || null,
        quantityKg: parseFloat(adjForm.quantityKg),
        adjustmentType: adjForm.adjustmentType,
        reason: adjForm.reason.trim(),
        notes: adjForm.notes.trim() || null,
      });
      setShowAdjModal(false);
      setAdjForm({ ingredientId: '', lotCode: '', quantityKg: '', adjustmentType: 'waste', reason: '', notes: '' });
      fetchAdjustments();
      fetchStock();
    } catch {
      setAdjError('Save failed. Please try again.');
    } finally {
      setAdjSaving(false);
    }
  };

  const handleToggleExpandCount = async (id) => {
    if (expandedCountId === id) { setExpandedCountId(null); setExpandedCountDetail(null); return; }
    setExpandedCountId(id);
    setExpandedCountDetail(null);
    try {
      const detail = await ingredientInventoryService.getPhysicalCount(id);
      setExpandedCountDetail(detail);
    } catch {
      setExpandedCountDetail({ lines: [] });
    }
  };

  const handleDeletePhysicalCount = async (id) => {
    const ok = await showConfirm('Delete Physical Count', 'This will reverse every adjustment this count created. This cannot be undone.', { confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      await ingredientInventoryService.deletePhysicalCount(id);
      if (expandedCountId === id) { setExpandedCountId(null); setExpandedCountDetail(null); }
      fetchPhysicalCounts();
      fetchStock();
    } catch (e) {
      await showAlert(e?.response?.data?.message || 'Failed to delete this physical count.', 'Delete Failed');
    }
  };

  const handleDeleteAdjustment = async (id) => {
    const ok = await showConfirm('Delete Adjustment', 'Delete this adjustment? This cannot be undone.', { confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      await ingredientInventoryService.deleteAdjustment(id);
      fetchAdjustments();
      fetchStock();
    } catch {
      // ignore
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Ingredient Inventory</h4>
        <div className="d-flex gap-2">
          {isAdmin && (
            <button
              className="btn btn-outline-secondary"
              onClick={() => setShowCheckConfig(true)}
              title="Configure acceptance checks"
            >
              ⚙
            </button>
          )}
          <button className="btn btn-outline-primary" onClick={() => scanCameraRef.current.click()}>📷 Take Photo</button>
          <input
            ref={scanCameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) setScanFile(f);
              e.target.value = '';
            }}
          />
          <button className="btn btn-outline-primary" onClick={() => scanFileRef.current.click()}>📎 Upload Receipt</button>
          <input
            ref={scanFileRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) setScanFile(f);
              e.target.value = '';
            }}
          />
          <button className="btn btn-primary" onClick={() => openNewLogModal()}>+ New Receiving Log</button>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'stock' ? ' active' : ''}`}
            onClick={() => setTab('stock')}
          >
            Stock
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'receiving' ? ' active' : ''}`}
            onClick={() => setTab('receiving')}
          >
            Receiving Logs
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'adjustments' ? ' active' : ''}`}
            onClick={() => setTab('adjustments')}
          >
            Adjustments
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'physicalCounts' ? ' active' : ''}`}
            onClick={() => setTab('physicalCounts')}
          >
            📋 Physical Count
          </button>
        </li>
      </ul>

      {tab === 'stock' && (
        <StockTab
          stock={stock}
          loading={stockLoading}
          onNewLog={openNewLogModal}
          stockRange={stockRange}
          setStockRange={setStockRange}
          onRangeChange={range => { setStockRange(range); fetchStock(range); }}
        />
      )}

      {tab === 'receiving' && (
        <ReceivingTab
          logs={logs}
          loading={logsLoading}
          ingredients={ingredients}
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          onEdit={openEditModal}
          onDelete={handleDelete}
          onOpenDocument={handleOpenDocument}
          onVerify={openVerifyModal}
          onDocUpload={openDocModal}
          onOpenDocById={(logId, docId) => ingredientInventoryService.getDocumentUrlById(logId, docId).then(r => window.open(r.url, '_blank')).catch(() => {})}
          onPrint={handlePrintLog}
          isAdmin={isAdmin}
          canVerify={canVerifyReceiving}
          qboConnected={!!companySettings?.qbRealmId}
          onPushQbo={(log) => setPushQboScanId(log.ReceiptScanImport.id)}
        />
      )}

      {tab === 'adjustments' && (
        <AdjustmentsTab
          adjustments={adjustments}
          loading={adjustmentsLoading}
          ingredients={ingredients}
          isAdmin={isAdmin}
          onNew={() => { setAdjError(''); setShowAdjModal(true); }}
          onDelete={handleDeleteAdjustment}
        />
      )}

      {tab === 'physicalCounts' && (
        <PhysicalCountHistoryTab
          sessions={physicalCounts}
          loading={physicalCountsLoading}
          isAdmin={isAdmin}
          expandedId={expandedCountId}
          expandedDetail={expandedCountDetail}
          onToggleExpand={handleToggleExpandCount}
          onDelete={handleDeletePhysicalCount}
          onStart={() => setShowPhysicalCountModal(true)}
        />
      )}

      {showPhysicalCountModal && (
        <IngredientPhysicalCountModal
          onClose={() => setShowPhysicalCountModal(false)}
          onCompleted={() => { setShowPhysicalCountModal(false); fetchPhysicalCounts(); fetchStock(); }}
        />
      )}

      {verifyLog && (
        <VerifyReceivingLogModal
          log={verifyLog}
          checkTemplates={checkTemplates}
          onClose={() => setVerifyLog(null)}
          onVerified={handleVerified}
        />
      )}

      {showDocModal && docModalLog && (
        <DocumentOnlyModal
          log={docModalLog}
          file={docModalFile}
          setFile={setDocModalFile}
          docType={docModalType}
          setDocType={setDocModalType}
          fileRef={docModalFileRef}
          saving={docModalSaving}
          error={docModalError}
          onSave={handleDocSave}
          onClose={() => setShowDocModal(false)}
          onDeleteDoc={(docId) => handleDeleteDocFromModal(docModalLog.id, docId)}
          onOpenDocById={(docId) => ingredientInventoryService.getDocumentUrlById(docModalLog.id, docId).then(r => window.open(r.url, '_blank')).catch(() => {})}
          extraOptions={ingredients.find(i => i.id === docModalLog.ingredientId)?.Manufacturer?.isForeign ? [PRIOR_NOTICE_OPTION] : []}
        />
      )}

      {showAdjModal && (
        <AdjustmentModal
          form={adjForm}
          setForm={setAdjForm}
          ingredients={ingredients}
          saving={adjSaving}
          error={adjError}
          onSave={handleCreateAdjustment}
          onClose={() => setShowAdjModal(false)}
        />
      )}

      {showModal && (
        <ReceivingLogModal
          form={form}
          setField={setField}
          setLotCode={setLotCode}
          addLotCode={addLotCode}
          removeLotCode={removeLotCode}
          totalQuantityKg={totalQuantityKg}
          checkTemplates={checkTemplates}
          setCheckResult={setCheckResult}
          ingredients={ingredients}
          suppliers={suppliers}
          fetchSuppliers={fetchSuppliers}
          editing={!!editingLog}
          saving={saving}
          formError={formError}
          pendingDocs={pendingDocs}
          setPendingDocs={setPendingDocs}
          savedDocuments={savedDocuments}
          onDeleteSavedDocument={handleDeleteSavedDocument}
          flushDocRef={flushDocRef}
          onSave={handleSave}
          onClose={closeModal}
          unit={form.unit}
          setUnit={v => setField('unit', v)}
          tempUnit={form.tempUnit}
          setTempUnit={v => setField('tempUnit', v)}
        />
      )}
      {showCheckConfig && (
        <CheckTemplateConfigModal onClose={() => setShowCheckConfig(false)} />
      )}
      <ConfirmModal dialog={dialog} onResolve={onResolveDialog} />
      {printLog && (
        <ReceivingRecordPrintModal
          log={printLog}
          companySettings={companySettings}
          onClose={() => setPrintLog(null)}
        />
      )}
      {priceHistoryPrompt && (
        <PriceHistoryPromptModal
          prompt={priceHistoryPrompt}
          onClose={() => setPriceHistoryPrompt(null)}
        />
      )}
      {scanFile && (
        <ReceiptScanReviewModal
          file={scanFile}
          ingredients={ingredients}
          suppliers={suppliers}
          fetchSuppliers={fetchSuppliers}
          onClose={() => setScanFile(null)}
          onConfirmed={() => {
            setScanFile(null);
            fetchStock();
            if (tab === 'receiving') fetchLogs(appliedFilters);
          }}
        />
      )}
      {pushQboScanId && (
        <PushReceiptToQboModal
          scanId={pushQboScanId}
          onClose={() => setPushQboScanId(null)}
          onPushed={() => {
            setPushQboScanId(null);
            if (tab === 'receiving') fetchLogs(appliedFilters);
          }}
        />
      )}
    </div>
  );
}

const STOCK_SORT_VALUE = (row, key) => {
  if (key === 'lastDeliveryDate') return row.lastDeliveryDate ? new Date(row.lastDeliveryDate).getTime() : null;
  return row[key];
};

function StockTab({ stock, loading, onNewLog, stockRange, onRangeChange }) {
  const [expanded, setExpanded] = useState({});   // { [ingredientId]: true }
  const [lotCodeCache, setLotCodeCache] = useState({}); // { [ingredientId]: [] | 'loading' | 'error' }
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const handleSort = makeSortHandler(setSort, ['name', 'rawMaterialCode']);
  const sortedStock = useMemo(() => sortRows(stock, sort, STOCK_SORT_VALUE), [stock, sort]);

  const toggleExpand = async (ingredientId) => {
    const isOpen = expanded[ingredientId];
    setExpanded(e => ({ ...e, [ingredientId]: !isOpen }));
    if (!isOpen && !lotCodeCache[ingredientId]) {
      setLotCodeCache(c => ({ ...c, [ingredientId]: 'loading' }));
      try {
        const data = await ingredientInventoryService.getLotCodesAvailable(ingredientId);
        setLotCodeCache(c => ({ ...c, [ingredientId]: Array.isArray(data) ? data : [] }));
      } catch {
        setLotCodeCache(c => ({ ...c, [ingredientId]: 'error' }));
      }
    }
  };

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    const fmt = d => d.toISOString().slice(0, 10);
    onRangeChange({ usedFrom: fmt(from), usedTo: fmt(to), preset: `${days}d` });
  };

  const handleCustomDate = (key, value) => {
    onRangeChange({ ...stockRange, [key]: value, preset: 'custom' });
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 bg-light rounded border">
        <span className="text-muted small fw-semibold me-1">Used in production:</span>
        {[30, 60, 90].map(d => (
          <button
            key={d}
            className={`btn btn-sm ${stockRange.preset === `${d}d` ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => applyPreset(d)}
          >
            Last {d}d
          </button>
        ))}
        <div className="d-flex align-items-center gap-1 ms-2">
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 140 }}
            value={stockRange.usedFrom || ''}
            onChange={e => handleCustomDate('usedFrom', e.target.value)}
          />
          <span className="text-muted small">–</span>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 140 }}
            value={stockRange.usedTo || ''}
            onChange={e => handleCustomDate('usedTo', e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
      <table className="table table-hover table-sm align-middle">
        <thead className="table-light">
          <tr>
            <SortableHeader label="Ingredient" sortKey="name" sort={sort} onSort={handleSort} />
            <SortableHeader label="RM Code" sortKey="rawMaterialCode" sort={sort} onSort={handleSort} />
            <SortableHeader label="Received (kg)" sortKey="receivedKg" sort={sort} onSort={handleSort} />
            <SortableHeader label="Used in Production (kg)" sortKey="usedKg" sort={sort} onSort={handleSort} />
            <SortableHeader label="Adjustments (kg)" sortKey="adjustedKg" sort={sort} onSort={handleSort} />
            <SortableHeader label="In Stock (kg)" sortKey="stockKg" sort={sort} onSort={handleSort} />
            <SortableHeader label="Last Delivery" sortKey="lastDeliveryDate" sort={sort} onSort={handleSort} />
            <th></th>
          </tr>
        </thead>
        <tbody>
          {stock.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-muted py-4">
                No stock data. Start by creating a receiving log.
              </td>
            </tr>
          ) : (
            sortedStock.map(row => {
              const isOpen = !!expanded[row.ingredientId];
              const lcs = lotCodeCache[row.ingredientId];
              return (
                <React.Fragment key={row.ingredientId}>
                  <tr>
                    <td>
                      <button
                        className="btn btn-link p-0 me-1 text-decoration-none"
                        style={{ fontSize: '0.7rem', lineHeight: 1 }}
                        onClick={() => toggleExpand(row.ingredientId)}
                        title={isOpen ? 'Collapse lot codes' : 'Expand lot codes'}
                      >
                        {isOpen ? '▼' : '▶'}
                      </button>
                      {row.name}
                    </td>
                    <td>
                      {row.rawMaterialCode ? (
                        <span className="badge bg-secondary">{row.rawMaterialCode}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{Number(row.receivedKg || 0).toFixed(1)}</td>
                    <td>{Number(row.usedKg || 0).toFixed(1)}</td>
                    <td className={Number(row.adjustedKg || 0) !== 0 ? (Number(row.adjustedKg) > 0 ? 'text-success' : 'text-danger') : 'text-muted'}>
                      {Number(row.adjustedKg || 0) > 0 ? '+' : ''}{Number(row.adjustedKg || 0).toFixed(1)}
                    </td>
                    <td className={stockColorClass(Number(row.stockKg || 0))}>
                      {Number(row.stockKg || 0).toFixed(1)}
                      {row.stockUnits != null && (
                        <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                          ({Math.round(row.stockUnits * 10) / 10} {row.caseLabel || 'units'})
                        </span>
                      )}
                    </td>
                    <td>
                      {row.lastDeliveryDate
                        ? new Date(row.lastDeliveryDate).toLocaleDateString()
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => onNewLog(String(row.ingredientId))}
                      >
                        Log Delivery
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={8} className="p-0 border-top-0">
                        <div className="px-4 py-2 bg-light border-bottom">
                          {lcs === 'loading' && (
                            <span className="text-muted small">Loading lot codes…</span>
                          )}
                          {lcs === 'error' && (
                            <span className="text-danger small">Could not load lot codes.</span>
                          )}
                          {Array.isArray(lcs) && lcs.length === 0 && (
                            <span className="text-muted small">No lot codes with remaining stock.</span>
                          )}
                          {Array.isArray(lcs) && lcs.length > 0 && (
                            <table className="table table-sm table-bordered mb-0" style={{ maxWidth: 620 }}>
                              <thead className="table-secondary">
                                <tr>
                                  <th className="small">Lot Code</th>
                                  <th className="small text-end">Received (kg)</th>
                                  <th className="small text-end">Used (kg)</th>
                                  <th className="small text-end">Adjustments (kg)</th>
                                  <th className="small text-end">Remaining (kg)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lcs.map(lc => {
                                  const adj = Number(lc.adjustedKg || 0);
                                  return (
                                    <tr key={lc.lotCode}>
                                      <td className="small"><span className="badge bg-light text-dark border">{lc.lotCode}</span></td>
                                      <td className="small text-end">{Number(lc.receivedKg).toFixed(3)}</td>
                                      <td className="small text-end">{Number(lc.usedKg).toFixed(3)}</td>
                                      <td className={`small text-end ${adj < 0 ? 'text-danger' : adj > 0 ? 'text-success' : 'text-muted'}`}>
                                        {adj !== 0 ? (adj > 0 ? '+' : '') + adj.toFixed(3) : '—'}
                                      </td>
                                      <td className="small text-end fw-semibold">{Number(lc.remainingKg).toFixed(3)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
      )}
    </>
  );
}

const RECEIVING_SORT_VALUE = (row, key) => {
  switch (key) {
    case 'deliveryDate': return row.deliveryDate ? parseDateOnly(row.deliveryDate).getTime() : null;
    case 'ingredientName': return row.Ingredient?.name || null;
    case 'rawMaterialCode': return row.Ingredient?.rawMaterialCode || null;
    case 'supplierName': return row.sourceType === 'internal' ? 'Internal Production' : (row.supplierName || null);
    case 'totalQuantityKg': return Number(row.totalQuantityKg || 0);
    case 'receivedTemperature': return row.receivedTemperature != null ? Number(row.receivedTemperature) : null;
    case 'receivedByName': return row.receivedByName || null;
    case 'verifiedAt': return row.verifiedAt ? new Date(row.verifiedAt).getTime() : null;
    default: return row[key];
  }
};

function ReceivingTab({ logs, loading, ingredients, filters, setFilters, onSearch, onClear, onEdit, onDelete, onOpenDocument, onVerify, onDocUpload, onOpenDocById, onPrint, isAdmin, canVerify, qboConnected, onPushQbo }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'deliveryDate', dir: 'desc' });
  const handleSort = makeSortHandler(setSort, ['ingredientName', 'rawMaterialCode', 'supplierName', 'receivedByName']);

  const visibleLogs = statusFilter === 'verified'
    ? logs.filter(l => l.verifiedAt)
    : statusFilter === 'pending'
      ? logs.filter(l => !l.verifiedAt)
      : logs;
  const sortedLogs = useMemo(() => sortRows(visibleLogs, sort, RECEIVING_SORT_VALUE), [visibleLogs, sort]);

  const pendingCount = logs.filter(l => !l.verifiedAt).length;

  return (
    <div>
      <div className="row g-2 mb-3 align-items-end">
        <div className="col-12 col-md-4">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search by supplier or lot code..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
          />
        </div>
        <div className="col-6 col-md-2">
          <SearchableSelect
            options={[
              { value: '', label: 'All Ingredients' },
              ...ingredients.map(ing => ({ value: ing.id, label: `${ing.name}${ing.rmCode ? ` (${ing.rmCode})` : ''}` })),
            ]}
            value={filters.ingredientId}
            onChange={v => setFilters(f => ({ ...f, ingredientId: v }))}
            placeholder="All Ingredients"
            size="sm"
          />
        </div>
        <div className="col-6 col-md-2">
          <input
            type="date"
            className="form-control form-control-sm"
            value={filters.dateFrom}
            onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
            placeholder="Date From"
          />
        </div>
        <div className="col-6 col-md-2">
          <input
            type="date"
            className="form-control form-control-sm"
            value={filters.dateTo}
            onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
            placeholder="Date To"
          />
        </div>
        <div className="col-6 col-md-2 d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={onSearch}>Search</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={onClear}>Clear</button>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="d-flex gap-2 mb-3">
        {[
          { key: 'all',      label: 'All',      variant: 'secondary' },
          { key: 'pending',  label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}`, variant: 'warning' },
          { key: 'verified', label: 'Verified', variant: 'success' },
        ].map(({ key, label, variant }) => (
          <button
            key={key}
            className={`btn btn-sm ${statusFilter === key ? `btn-${variant}` : `btn-outline-${variant}`}`}
            onClick={() => setStatusFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle">
            <thead className="table-light">
              <tr>
                <SortableHeader label="Date" sortKey="deliveryDate" sort={sort} onSort={handleSort} />
                <SortableHeader label="Ingredient" sortKey="ingredientName" sort={sort} onSort={handleSort} />
                <SortableHeader label="RM Code" sortKey="rawMaterialCode" sort={sort} onSort={handleSort} />
                <SortableHeader label="Supplier" sortKey="supplierName" sort={sort} onSort={handleSort} />
                <th>Lot Codes</th>
                <SortableHeader label="Qty (kg)" sortKey="totalQuantityKg" sort={sort} onSort={handleSort} />
                <SortableHeader label="Temp" sortKey="receivedTemperature" sort={sort} onSort={handleSort} />
                <th>Document</th>
                <SortableHeader label="Received By" sortKey="receivedByName" sort={sort} onSort={handleSort} />
                <SortableHeader label="Status" sortKey="verifiedAt" sort={sort} onSort={handleSort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleLogs.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center text-muted py-4">
                    {statusFilter === 'pending' ? 'No pending records — all verified!' : statusFilter === 'verified' ? 'No verified records yet.' : 'No receiving logs found.'}
                  </td>
                </tr>
              ) : (
                sortedLogs.map(log => (
                  <tr key={log.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {log.deliveryDate ? parseDateOnly(log.deliveryDate).toLocaleDateString() : '—'}
                    </td>
                    <td>{log.Ingredient?.name || log.ingredientId}</td>
                    <td>{log.Ingredient?.rawMaterialCode || '—'}</td>
                    <td>
                      {log.sourceType === 'internal' ? (
                        <span className="badge bg-info text-dark" title="Auto-credited from a Prep CCP run">
                          🏭 Internal Production
                        </span>
                      ) : log.supplierName}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(log.LotCodes || []).map((lc, i) => (
                          <span key={i} className="badge bg-light text-dark border">{lc.lotCode}</span>
                        ))}
                      </div>
                    </td>
                    <td>{Number(log.totalQuantityKg || 0).toFixed(1)}</td>
                    <td>
                      {log.isTemperatureControlled ? (
                        <>
                          {log.isFrozen && <span title="Frozen">&#10052; </span>}
                          {log.receivedTemperature != null ? `${log.receivedTemperature}°C` : '—'}
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      {(log.Documents || []).length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {(log.Documents || []).map(doc => (
                            <button
                              key={doc.id}
                              className="btn btn-link p-0 border-0"
                              style={{ lineHeight: 1 }}
                              onClick={() => onOpenDocById(log.id, doc.id)}
                              title={`Open ${docTypeLabel(doc.documentType)}`}
                            >
                              <span className="badge bg-info text-dark">&#128206; {docTypeLabel(doc.documentType)}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{log.receivedByName || '—'}</td>
                    <td>
                      {log.verifiedAt ? (
                        <span className="badge bg-success" title={`Verified by ${log.verifiedByName || '?'} on ${new Date(log.verifiedAt).toLocaleDateString()}`}>
                          Verified
                        </span>
                      ) : (
                        <span className="badge bg-secondary">Pending</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {!log.verifiedAt && (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => onEdit(log)}
                            title="Edit"
                          >
                            &#9998;
                          </button>
                        )}
                        {log.verifiedAt && (
                          <button
                            className="btn btn-outline-info btn-sm"
                            onClick={() => onDocUpload(log)}
                            title="Upload document"
                          >
                            &#128206;
                          </button>
                        )}
                        {canVerify && !log.verifiedAt && (
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => onVerify(log.id)}
                            title="Verify and lock this log"
                          >
                            &#10003;
                          </button>
                        )}
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => onPrint(log.id)}
                          title="Print / export record"
                        >
                          &#128424;
                        </button>
                        {isAdmin && qboConnected && log.ReceiptScanImport && !log.ReceiptScanImport.qboPurchaseId && (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => onPushQbo(log)}
                            title="Push this receipt to QuickBooks"
                          >
                            <span style={{ fontWeight: 700 }}>QB</span> Push
                          </button>
                        )}
                        {!log.verifiedAt && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => onDelete(log.id)}
                            title="Delete"
                          >
                            &#128465;
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReceivingLogModal({
  form, setField, setLotCode, addLotCode, removeLotCode, totalQuantityKg,
  checkTemplates, setCheckResult,
  ingredients, suppliers, fetchSuppliers, editing, saving, formError,
  pendingDocs, setPendingDocs, savedDocuments, onDeleteSavedDocument,
  flushDocRef,
  onSave, onClose, unit, setUnit, tempUnit, setTempUnit,
}) {
  const selectedIngredient = ingredients.find(i => String(i.id) === String(form.ingredientId));
  const isForeignManufacturer = !!selectedIngredient?.Manufacturer?.isForeign;
  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editing ? 'Edit Receiving Log' : 'New Receiving Log'}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>

            <div className="modal-body">
              {/* Section 1 — Core */}
              <h6 className="fw-semibold mb-2">Delivery Details</h6>
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label">Ingredient <span className="text-danger">*</span></label>
                  <IngredientSearch
                    ingredients={ingredients}
                    value={form.ingredientId}
                    onChange={v => setField('ingredientId', v)}
                  />
                  {form.ingredientId && (() => {
                    const ing = ingredients.find(i => String(i.id) === String(form.ingredientId));
                    return ing?.Manufacturer?.name ? (
                      <div className="form-text mt-1">
                        <span className="text-muted">Manufacturer:</span> <strong>{ing.Manufacturer.name}</strong>
                      </div>
                    ) : null;
                  })()}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Delivery Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.deliveryDate}
                    onChange={e => setField('deliveryDate', e.target.value)}
                    min="2015-01-01"
                    max={maxDeliveryDate}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Unit Cost / {unit}</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <MathInput
                      className="form-control"
                      value={form.unitCost}
                      onChange={v => setField('unitCost', v)}
                      onCommit={v => setField('unitCost', v)}
                      placeholder="e.g. 1.5 or 3/2"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Supplier <span className="text-danger">*</span></label>
                  <SupplierPickerField
                    options={suppliers}
                    value={form.supplierId}
                    onChange={v => setField('supplierId', v)}
                    onCreated={fetchSuppliers}
                    placeholder="Select or type to add a supplier…"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Received By</label>
                  <input
                    type="text"
                    className={`form-control${!form.receivedByName.trim() ? ' is-warning' : ''}`}
                    value={form.receivedByName}
                    onChange={e => setField('receivedByName', e.target.value)}
                    placeholder="Your name"
                  />
                  {!form.receivedByName.trim() && (
                    <div className="form-text text-warning">
                      &#9888; Name is empty — it will be blank on the record. Enter your name for audit traceability.
                    </div>
                  )}
                </div>
              </div>

              <hr />

              {/* Section 2 — Lot Codes */}
              <div className="d-flex align-items-center gap-3 mb-2">
                <h6 className="fw-semibold mb-0">Lot Codes</h6>
                <div className="btn-group btn-group-sm" role="group" aria-label="Unit">
                  <button
                    type="button"
                    className={`btn ${unit === 'kg' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setUnit('kg')}
                  >kg</button>
                  <button
                    type="button"
                    className={`btn ${unit === 'lb' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setUnit('lb')}
                  >lb</button>
                </div>
                {unit === 'lb' && (
                  <span className="text-muted small">Quantities and cost entered in lb — stored as kg</span>
                )}
              </div>
              {form.lotCodes.map((lc, i) => (
                <div key={i} className="row g-2 mb-2 align-items-center">
                  <div className="col">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Lot code"
                      value={lc.lotCode}
                      onChange={e => setLotCode(i, 'lotCode', e.target.value)}
                    />
                  </div>
                  <div className="col-auto" style={{ width: 180 }}>
                    <div className="input-group">
                      <MathInput
                        className="form-control"
                        placeholder="Qty"
                        value={lc.quantityKg}
                        onChange={v => setLotCode(i, 'quantityKg', v)}
                        onCommit={v => setLotCode(i, 'quantityKg', v)}
                      />
                      <span className="input-group-text">{unit}</span>
                    </div>
                  </div>
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeLotCode(i)}
                      disabled={form.lotCodes.length <= 1}
                    >
                      &times;
                    </button>
                  </div>
                </div>
              ))}
              <div className="d-flex align-items-center gap-3 mb-4">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addLotCode}>
                  + Add Lot Code
                </button>
                <span className="text-muted small">
                  Total: <strong>{totalQuantityKg.toFixed(3)} kg</strong>
                </span>
              </div>

              <hr />

              {/* Section 3 — Temperature */}
              <h6 className="fw-semibold mb-2">Temperature</h6>
              <div className="mb-3">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="chkTempControlled"
                    checked={form.isTemperatureControlled}
                    onChange={e => setField('isTemperatureControlled', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="chkTempControlled">Temperature Controlled</label>
                </div>

                {form.isTemperatureControlled && (
                  <div className="ms-4">
                    <div className="mb-2" style={{ maxWidth: 240 }}>
                      <div className="input-group">
                        <MathInput
                          className="form-control"
                          placeholder="Temperature"
                          value={form.receivedTemperature}
                          onChange={v => setField('receivedTemperature', v)}
                          onCommit={v => setField('receivedTemperature', v)}
                        />
                        <button
                          type="button"
                          className={`btn btn-sm ${tempUnit === 'C' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                          onClick={() => setTempUnit('C')}
                        >°C</button>
                        <button
                          type="button"
                          className={`btn btn-sm ${tempUnit === 'F' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                          onClick={() => setTempUnit('F')}
                        >°F</button>
                      </div>
                      {tempUnit === 'F' && (
                        <div className="form-text">Stored as °C</div>
                      )}
                    </div>
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="chkFrozen"
                        checked={form.isFrozen}
                        onChange={e => setField('isFrozen', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="chkFrozen">Frozen Product</label>
                    </div>
                    {form.isFrozen && (
                      <div className="ms-4">
                        <div className="form-check mb-1">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="chkFrozenConfirmed"
                            checked={form.checkFrozenIsFrozen}
                            onChange={e => setField('checkFrozenIsFrozen', e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="chkFrozenConfirmed">Product is confirmed frozen</label>
                        </div>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="chkNoThawing"
                            checked={form.checkFrozenNoThawing}
                            onChange={e => setField('checkFrozenNoThawing', e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="chkNoThawing">No signs of thawing observed</label>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <hr />

              {/* Section 4 — Acceptance Checks */}
              {checkTemplates.length > 0 && (
                <>
                  <h6 className="fw-semibold mb-2">Acceptance Checks</h6>
                  <div className="mb-4">
                    {checkTemplates
                      .filter(t => conditionVisible(t.condition, form))
                      .map(t => {
                        const cr = form.checkResults.find(r => r.checkTemplateId === t.id);
                        if (!cr) return null;
                        return (
                          <div key={t.id} className="card mb-2">
                            <div className="card-body py-2 px-3">
                              <div className="d-flex align-items-start gap-3 flex-wrap">
                                <div className="flex-grow-1">
                                  <div className="fw-medium">{t.label}</div>
                                  {t.description && (
                                    <div className="text-muted small">{t.description}</div>
                                  )}
                                  {t.condition !== 'always' && (
                                    <div className="text-muted small fst-italic">{conditionHint(t.condition)}</div>
                                  )}
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${cr.passed ? 'btn-success' : 'btn-outline-success'}`}
                                    onClick={() => setCheckResult(t.id, 'passed', true)}
                                  >
                                    Pass
                                  </button>
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${!cr.passed ? 'btn-danger' : 'btn-outline-danger'}`}
                                    onClick={() => setCheckResult(t.id, 'passed', false)}
                                  >
                                    Fail
                                  </button>
                                </div>
                              </div>
                              <div className="mt-2">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Notes (optional)"
                                  value={cr.notes}
                                  onChange={e => setCheckResult(t.id, 'notes', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  <hr />
                </>
              )}

              {/* Section 5 — Rejection */}
              <h6 className="fw-semibold mb-2">Rejection</h6>
              <div className="mb-4">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="chkRejection"
                    checked={form.hasRejection}
                    onChange={e => setField('hasRejection', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="chkRejection">Any product rejected?</label>
                </div>
                {form.hasRejection && (
                  <div className="ms-4">
                    <div className="mb-2">
                      <label className="form-label mb-1">Rejection Description</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.rejectionDescription}
                        onChange={e => setField('rejectionDescription', e.target.value)}
                        placeholder="Describe what was rejected and why"
                      />
                    </div>
                    <div style={{ maxWidth: 220 }}>
                      <label className="form-label mb-1">Rejected Qty ({unit})</label>
                      <div className="input-group">
                        <MathInput
                          className="form-control"
                          placeholder="Qty"
                          value={form.rejectedQuantityKg}
                          onChange={v => setField('rejectedQuantityKg', v)}
                          onCommit={v => setField('rejectedQuantityKg', v)}
                        />
                        <span className="input-group-text">{unit}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <hr />

              {/* Section 6 — Deviations */}
              <h6 className="fw-semibold mb-2">Deviations &amp; Corrective Actions</h6>
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label">Deviation</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.deviation}
                    onChange={e => setField('deviation', e.target.value)}
                    placeholder="Describe any deviations observed"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Corrective Action</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.correctiveAction}
                    onChange={e => setField('correctiveAction', e.target.value)}
                    placeholder="Describe corrective actions taken"
                  />
                </div>
              </div>

              <hr />

              {/* Section 7 — Documents */}
              <h6 className="fw-semibold mb-2">Documents</h6>
              <DocumentsSection
                savedDocuments={savedDocuments}
                pendingDocs={pendingDocs}
                setPendingDocs={setPendingDocs}
                onDeleteSaved={onDeleteSavedDocument}
                flushDocRef={flushDocRef}
                extraOptions={isForeignManufacturer ? [PRIOR_NOTICE_OPTION] : []}
              />

              <hr />

              {/* Section 8 — Verification */}
              <h6 className="fw-semibold mb-2">Verification</h6>
              {/* Verified By / Verified At are read-only here on purpose — they must only ever be
                  set atomically, together, by the real Verify action (which also attributes a real
                  user id). A free-typed date here previously let a record get locked with a wrong
                  date and no verifier attached (no verifiedByUserId at all). */}
              {form.verifiedAt ? (
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Verified By</label>
                    <input type="text" className="form-control" value={form.verifiedByName || '—'} disabled />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Verified At</label>
                    <input type="text" className="form-control" value={new Date(form.verifiedAt).toLocaleString()} disabled />
                  </div>
                </div>
              ) : (
                <div className="alert alert-info py-2 small mb-3">
                  Not yet verified. Use the <strong>Verify</strong> button on the log list to record
                  acceptance checks, receiving temperature, and sign this record — it will be
                  attributed to your logged-in account automatically.
                </div>
              )}
              <div className="row g-3 mb-3">
                <div className="col-md-12">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.notes}
                    onChange={e => setField('notes', e.target.value)}
                    placeholder="Any additional notes"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {formError && (
                <div className="text-danger me-auto small">{formError}</div>
              )}
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving…
                  </>
                ) : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Verifying a receiving log is a single, atomic, admin-only action — acceptance checks and
// receiving temperature are captured HERE, not via a separate detour through Edit. Every
// applicable check starts unanswered (not auto-passed) so verifying always requires an explicit
// decision, even for logs (e.g. from AI Receipt Scan) that never had checks recorded at all.
function VerifyReceivingLogModal({ log, checkTemplates, onClose, onVerified }) {
  const [checkResults, setCheckResults] = useState(() => {
    const existingByTemplate = Object.fromEntries((log.CheckResults || []).map(cr => [cr.checkTemplateId, cr]));
    return checkTemplates.map(t => {
      const existing = existingByTemplate[t.id];
      return {
        checkTemplateId: t.id,
        passed: existing ? existing.passed : null, // null = unanswered, forces an explicit choice
        notes: existing?.notes || '',
      };
    });
  });
  const [isTemperatureControlled, setIsTemperatureControlled] = useState(!!log.isTemperatureControlled);
  const [receivedTemperature, setReceivedTemperature] = useState(log.receivedTemperature != null ? String(log.receivedTemperature) : '');
  const [tempUnit, setTempUnit] = useState('C');
  const [verifiedByName, setVerifiedByName] = useState(getLoggedInUsername());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const pseudoForm = { isTemperatureControlled };
  const visibleTemplates = checkTemplates.filter(t => conditionVisible(t.condition, pseudoForm));
  const setCheck = (templateId, field, value) => {
    setCheckResults(prev => prev.map(cr => (cr.checkTemplateId === templateId ? { ...cr, [field]: value } : cr)));
  };

  const unanswered = visibleTemplates.filter(t => {
    const cr = checkResults.find(r => r.checkTemplateId === t.id);
    return !cr || cr.passed == null;
  });

  const handleSubmit = async () => {
    if (unanswered.length > 0) {
      setError(`Please answer every check: ${unanswered.map(t => t.label).join(', ')}.`);
      return;
    }
    setError('');
    setSaving(true);
    try {
      const toC = (v) => tempUnit === 'F' ? (parseFloat(v) - 32) * 5 / 9 : parseFloat(v);
      await ingredientInventoryService.verifyReceivingLog(log.id, {
        verifiedByName: verifiedByName.trim() || null,
        checkResults: visibleTemplates.map(t => {
          const cr = checkResults.find(r => r.checkTemplateId === t.id);
          return { checkTemplateId: t.id, passed: cr.passed, notes: cr.notes.trim() || null };
        }),
        isTemperatureControlled,
        receivedTemperature: isTemperatureControlled && receivedTemperature !== '' ? toC(receivedTemperature) : null,
        notes: notes.trim() || null,
      });
      onVerified();
    } catch (e) {
      setError(e?.response?.data?.message || 'Verification failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">✓ Verify Receiving Log</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2 small">{error}</div>}
            <div className="text-muted small mb-3">
              {log.Ingredient?.name || log.ingredientId} — {log.supplierName} — {log.deliveryDate} — {Number(log.totalQuantityKg).toFixed(2)} kg
            </div>

            <h6 className="fw-semibold mb-2">Receiving Temperature</h6>
            <div className="mb-3">
              <div className="form-check mb-2">
                <input
                  type="checkbox" className="form-check-input" id="verifyTempControlled"
                  checked={isTemperatureControlled} onChange={e => setIsTemperatureControlled(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="verifyTempControlled">Temperature Controlled</label>
              </div>
              {isTemperatureControlled && (
                <div className="ms-4" style={{ maxWidth: 240 }}>
                  <div className="input-group">
                    <MathInput
                      className="form-control" placeholder="Temperature"
                      value={receivedTemperature} onChange={setReceivedTemperature} onCommit={setReceivedTemperature}
                    />
                    <button type="button" className={`btn btn-sm ${tempUnit === 'C' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setTempUnit('C')}>°C</button>
                    <button type="button" className={`btn btn-sm ${tempUnit === 'F' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setTempUnit('F')}>°F</button>
                  </div>
                </div>
              )}
            </div>

            <hr />

            <h6 className="fw-semibold mb-2">Acceptance Checks</h6>
            {visibleTemplates.length === 0 ? (
              <div className="text-muted small mb-3">No check templates configured.</div>
            ) : (
              <div className="mb-3">
                {visibleTemplates.map(t => {
                  const cr = checkResults.find(r => r.checkTemplateId === t.id);
                  return (
                    <div key={t.id} className="card mb-2">
                      <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-start gap-3 flex-wrap">
                          <div className="flex-grow-1">
                            <div className="fw-medium">{t.label}</div>
                            {t.description && <div className="text-muted small">{t.description}</div>}
                          </div>
                          <div className="d-flex gap-2 align-items-center">
                            <button type="button" className={`btn btn-sm ${cr.passed === true ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setCheck(t.id, 'passed', true)}>Pass</button>
                            <button type="button" className={`btn btn-sm ${cr.passed === false ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => setCheck(t.id, 'passed', false)}>Fail</button>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text" className="form-control form-control-sm" placeholder="Notes (optional)"
                            value={cr.notes} onChange={e => setCheck(t.id, 'notes', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <hr />

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Verified By</label>
                <input type="text" className="form-control" value={verifiedByName} onChange={e => setVerifiedByName(e.target.value)} />
              </div>
              <div className="col-md-12">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-success" onClick={handleSubmit} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Verifying…</> : 'Verify'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const DOC_TYPE_OPTIONS = [
  { value: 'bol', label: 'BOL (Bill of Lading)' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'other', label: 'Other' },
];

// Only offered when the delivery's ingredient has a foreign manufacturer — see
// docs/SUPPLIERS_MODULE.md (Prior Notice is tracked per shipment, not per manufacturer).
const PRIOR_NOTICE_OPTION = { value: 'prior_notice', label: 'Prior Notice Confirmation' };

function DocumentsSection({ savedDocuments, pendingDocs, setPendingDocs, onDeleteSaved, flushDocRef, extraOptions = [] }) {
  const [newType, setNewType] = useState('');
  const [newFile, setNewFile] = useState(null);
  const fileRef = useRef();
  const typeOptions = [...DOC_TYPE_OPTIONS, ...extraOptions];

  // Expose a flush function so the parent can auto-include any staged-but-not-added doc on save
  useEffect(() => {
    if (flushDocRef) {
      flushDocRef.current = () => {
        if (newType && newFile) return { documentType: newType, file: newFile, _key: Date.now() };
        return null;
      };
    }
  }, [flushDocRef, newType, newFile]);

  const addPending = () => {
    if (!newType || !newFile) return;
    setPendingDocs(d => [...d, { documentType: newType, file: newFile, _key: Date.now() }]);
    setNewType('');
    setNewFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePending = (key) => setPendingDocs(d => d.filter(pd => pd._key !== key));

  return (
    <div className="mb-4">
      {(savedDocuments.length > 0 || pendingDocs.length > 0) && (
        <ul className="list-group mb-3">
          {savedDocuments.map(doc => (
            <li key={doc.id} className="list-group-item d-flex align-items-center gap-2 py-2">
              <span className="badge bg-info text-dark">&#128206; {docTypeLabel(doc.documentType)}</span>
              <span className="text-muted small flex-grow-1">{doc.documentOriginalName}</span>
              {onDeleteSaved && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm py-0 px-1"
                  style={{ lineHeight: 1.2 }}
                  onClick={() => onDeleteSaved(doc.id)}
                  title="Remove document"
                >&times;</button>
              )}
            </li>
          ))}
          {pendingDocs.map(pd => (
            <li key={pd._key} className="list-group-item d-flex align-items-center gap-2 py-2 list-group-item-warning">
              <span className="badge bg-warning text-dark">&#128206; {docTypeLabel(pd.documentType)}</span>
              <span className="text-muted small flex-grow-1">{pd.file.name} <em>(pending upload)</em></span>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm py-0 px-1"
                style={{ lineHeight: 1.2 }}
                onClick={() => removePending(pd._key)}
              >&times;</button>
            </li>
          ))}
        </ul>
      )}
      <div className="row g-2 align-items-end">
        <div className="col-auto">
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={newType}
            onChange={e => setNewType(e.target.value)}
          >
            <option value="">Document type…</option>
            {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="col">
          <input
            ref={fileRef}
            type="file"
            className="form-control form-control-sm"
            disabled={!newType}
            onChange={e => setNewFile(e.target.files?.[0] || null)}
          />
        </div>
        <div className="col-auto">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={!newType || !newFile}
            onClick={addPending}
          >
            + Add
          </button>
        </div>
      </div>
      <div className="form-text">Documents are uploaded when you save the log.</div>
    </div>
  );
}

function DocumentOnlyModal({ log, file, setFile, docType, setDocType, fileRef, saving, error, onSave, onClose, onDeleteDoc, onOpenDocById, extraOptions = [] }) {
  const typeOptions = [...DOC_TYPE_OPTIONS, ...extraOptions];
  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Manage Documents</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              <div className="alert alert-info py-2 small mb-3">
                This log is <strong>verified and locked</strong>. Only documents can be added or removed.
              </div>
              {(log.Documents || []).length > 0 && (
                <ul className="list-group mb-3">
                  {(log.Documents || []).map(doc => (
                    <li key={doc.id} className="list-group-item d-flex align-items-center gap-2 py-2">
                      <button
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={() => onOpenDocById(doc.id)}
                      >
                        <span className="badge bg-info text-dark">&#128206; {docTypeLabel(doc.documentType)}</span>
                      </button>
                      <span className="text-muted small flex-grow-1">{doc.documentOriginalName}</span>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm py-0 px-1"
                        style={{ lineHeight: 1.2 }}
                        onClick={() => onDeleteDoc(doc.id)}
                        title="Remove document"
                      >&times;</button>
                    </li>
                  ))}
                </ul>
              )}
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <p className="text-muted small mb-2">Add a new document:</p>
              <div className="mb-3">
                <select
                  className="form-select mb-2"
                  value={docType}
                  onChange={e => setDocType(e.target.value)}
                >
                  <option value="">— Document type —</option>
                  {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {docType && (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      className="form-control"
                      onChange={e => setFile(e.target.files?.[0] || null)}
                    />
                    {file && (
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <span className="form-text mb-0">{file.name}</span>
                        <button type="button" className="btn btn-outline-danger btn-sm py-0 px-1" style={{ lineHeight: 1.2 }}
                          onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                        >&times;</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
              <button className="btn btn-primary" onClick={onSave} disabled={saving || !file || !docType}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Uploading…</> : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const ADJ_TYPE_LABELS = {
  waste: 'Waste',
  damage: 'Damage',
  sample: 'Sample',
  correction: 'Correction',
  resale: 'Resale',
  other: 'Other',
};

// These types can only ever reduce stock — a user naturally types the positive magnitude
// ("wasted 3kg" → "3"), so the sign is forced negative automatically instead of silently adding
// stock when they forget the minus sign. "Correction" and "Other" stay free-sign since a
// correction can legitimately go either direction (e.g. a recount found more stock than expected).
const ALWAYS_REDUCTION_TYPES = new Set(['waste', 'damage', 'sample', 'resale']);

function AdjustmentsTab({ adjustments, loading, ingredients, isAdmin, onNew, onDelete }) {
  const ingById = (id) => ingredients.find(i => i.id === Number(id));
  const [sort, setSort] = useState({ key: 'adjustedAt', dir: 'desc' });
  const handleSort = makeSortHandler(setSort, ['ingredientName', 'lotCode', 'adjustmentType', 'reason', 'adjustedByName']);
  const sortValue = (adj, key) => {
    switch (key) {
      case 'adjustedAt': return adj.adjustedAt ? new Date(adj.adjustedAt).getTime() : null;
      case 'ingredientName': return ingById(adj.ingredientId)?.name || null;
      case 'quantityKg': return Number(adj.quantityKg);
      default: return adj[key];
    }
  };
  const sortedAdjustments = useMemo(() => sortRows(adjustments, sort, sortValue), [adjustments, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">
          Record waste, damage, samples, or corrections to ingredient stock. Each adjustment is logged with a reason for traceability.
        </span>
        <button className="btn btn-primary btn-sm" onClick={onNew}>+ New Adjustment</button>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle">
            <thead className="table-light">
              <tr>
                <SortableHeader label="Date" sortKey="adjustedAt" sort={sort} onSort={handleSort} />
                <SortableHeader label="Ingredient" sortKey="ingredientName" sort={sort} onSort={handleSort} />
                <SortableHeader label="Lot Code" sortKey="lotCode" sort={sort} onSort={handleSort} />
                <SortableHeader label="Type" sortKey="adjustmentType" sort={sort} onSort={handleSort} />
                <SortableHeader label="Qty (kg)" sortKey="quantityKg" sort={sort} onSort={handleSort} />
                <SortableHeader label="Reason" sortKey="reason" sort={sort} onSort={handleSort} />
                <SortableHeader label="By" sortKey="adjustedByName" sort={sort} onSort={handleSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {adjustments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No adjustments recorded yet.
                  </td>
                </tr>
              ) : (
                sortedAdjustments.map(adj => {
                  const ing = ingById(adj.ingredientId);
                  return (
                    <tr key={adj.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {adj.adjustedAt ? new Date(adj.adjustedAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        {ing ? ing.name : adj.ingredientId}
                        {ing?.rawMaterialCode && (
                          <span className="ms-1 badge bg-secondary">{ing.rawMaterialCode}</span>
                        )}
                      </td>
                      <td>{adj.lotCode || <span className="text-muted">—</span>}</td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {ADJ_TYPE_LABELS[adj.adjustmentType] || adj.adjustmentType}
                        </span>
                      </td>
                      <td className={Number(adj.quantityKg) >= 0 ? 'text-success' : 'text-danger'}>
                        {Number(adj.quantityKg) > 0 ? '+' : ''}{Number(adj.quantityKg).toFixed(1)}
                      </td>
                      <td>{adj.reason}</td>
                      <td>{adj.adjustedByName || '—'}</td>
                      <td>
                        {isAdmin && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => onDelete(adj.id)}
                            title="Delete (admin only)"
                          >
                            Del
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PHYSICAL_COUNT_SORT_VALUE = (row, key) => {
  if (key === 'countedAt') return row.countedAt ? new Date(row.countedAt).getTime() : null;
  return row[key];
};

function PhysicalCountHistoryTab({ sessions, loading, isAdmin, expandedId, expandedDetail, onToggleExpand, onDelete, onStart }) {
  const [sort, setSort] = useState({ key: 'countedAt', dir: 'desc' });
  const handleSort = makeSortHandler(setSort, ['performedByName', 'notes']);
  const sortedSessions = useMemo(() => sortRows(sessions, sort, PHYSICAL_COUNT_SORT_VALUE), [sessions, sort]);
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">
          Reconcile ingredient stock against a real physical count — weekly, monthly, or whenever
          you want. Any deviation auto-creates a tracked adjustment with a reason, credited to you.
        </span>
        <button className="btn btn-primary btn-sm" onClick={onStart}>▶ Start New Count</button>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle">
            <thead className="table-light">
              <tr>
                <SortableHeader label="Date" sortKey="countedAt" sort={sort} onSort={handleSort} />
                <SortableHeader label="Performed By" sortKey="performedByName" sort={sort} onSort={handleSort} />
                <SortableHeader label="Items Counted" sortKey="lineCount" sort={sort} onSort={handleSort} />
                <SortableHeader label="Adjustments" sortKey="adjustmentCount" sort={sort} onSort={handleSort} />
                <SortableHeader label="Notes" sortKey="notes" sort={sort} onSort={handleSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">No physical counts recorded yet.</td>
                </tr>
              ) : (
                sortedSessions.map(s => (
                  <React.Fragment key={s.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => onToggleExpand(s.id)}>
                      <td style={{ whiteSpace: 'nowrap' }}>{s.countedAt ? new Date(s.countedAt).toLocaleDateString() : '—'}</td>
                      <td>{s.performedByName || '—'}</td>
                      <td>{s.lineCount}</td>
                      <td>
                        {s.adjustmentCount > 0
                          ? <span className="badge bg-warning text-dark">{s.adjustmentCount} adjustment{s.adjustmentCount === 1 ? '' : 's'}</span>
                          : <span className="badge bg-success">All matched</span>}
                      </td>
                      <td>{s.notes || <span className="text-muted">—</span>}</td>
                      <td className="text-end">
                        <span className="me-2">{expandedId === s.id ? '▲' : '▼'}</span>
                        {isAdmin && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                            title="Delete (admin only) — reverses its adjustments"
                          >
                            Del
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === s.id && (
                      <tr>
                        <td colSpan={6} className="bg-light">
                          {!expandedDetail ? (
                            <div className="text-center py-2"><div className="spinner-border spinner-border-sm text-primary" /></div>
                          ) : (
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>Ingredient</th>
                                  <th>Lot</th>
                                  <th>System (kg)</th>
                                  <th>Counted (kg)</th>
                                  <th>Delta</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(expandedDetail.lines || []).map(line => (
                                  <tr key={line.id}>
                                    <td>{line.Ingredient?.name || line.ingredientId}</td>
                                    <td>{line.lotCode || <span className="text-muted">—</span>}</td>
                                    <td>{Number(line.systemQuantityKg).toFixed(2)}</td>
                                    <td>{Number(line.countedKg).toFixed(2)}</td>
                                    <td className={Number(line.deltaKg) === 0 ? 'text-muted' : Number(line.deltaKg) > 0 ? 'text-success' : 'text-danger'}>
                                      {Number(line.deltaKg) > 0 ? '+' : ''}{Number(line.deltaKg).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdjustmentModal({ form, setForm, ingredients, saving, error, onSave, onClose }) {
  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const [lotCodes, setLotCodes] = useState([]);
  const [lotCodesLoading, setLotCodesLoading] = useState(false);
  const [currentStock, setCurrentStock] = useState(null);

  // Fetch lot codes and all-time stock when ingredient changes
  useEffect(() => {
    if (!form.ingredientId) { setLotCodes([]); setCurrentStock(null); return; }
    setLotCodesLoading(true);
    setCurrentStock(null);
    Promise.all([
      ingredientInventoryService.getLotCodesAvailable(form.ingredientId).catch(() => []),
      ingredientInventoryService.getStock({}).catch(() => []),
    ]).then(([lcs, stockRows]) => {
      setLotCodes(Array.isArray(lcs) ? lcs : []);
      const row = (stockRows || []).find(s => String(s.ingredientId) === String(form.ingredientId));
      setCurrentStock(row ? Number(row.stockKg || 0) : 0);
    }).finally(() => setLotCodesLoading(false));
    set('lotCode', '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ingredientId]);

  const adjQty = parseFloat(form.quantityKg);
  const afterStock = currentStock !== null && !isNaN(adjQty)
    ? currentStock + adjQty
    : null;

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">New Inventory Adjustment</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="mb-3">
                <label className="form-label">Ingredient <span className="text-danger">*</span></label>
                <SearchableSelect
                  options={ingredients.map(ing => ({
                    value: ing.id,
                    label: `${ing.name}${ing.rawMaterialCode ? ` (${ing.rawMaterialCode})` : ''}`,
                  }))}
                  value={form.ingredientId}
                  onChange={v => set('ingredientId', v)}
                  placeholder="Select ingredient…"
                />
              </div>

              {/* Stock summary */}
              {form.ingredientId && currentStock !== null && (
                <div className={`alert py-2 mb-3 d-flex justify-content-between align-items-center ${afterStock !== null && afterStock < 0 ? 'alert-danger' : 'alert-info'}`}>
                  <span>
                    <span className="text-muted small">Current stock:</span>{' '}
                    <strong>{currentStock.toFixed(3)} kg</strong>
                  </span>
                  {afterStock !== null && (
                    <span>
                      <span className="text-muted small">After adjustment:</span>{' '}
                      <strong className={afterStock < 0 ? 'text-danger' : afterStock === 0 ? 'text-warning' : 'text-success'}>
                        {afterStock.toFixed(3)} kg
                      </strong>
                    </span>
                  )}
                </div>
              )}

              {/* Lot code */}
              <div className="mb-3">
                <label className="form-label">Lot Code</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Optional — type or select below"
                  value={form.lotCode}
                  onChange={e => set('lotCode', e.target.value)}
                />
                {lotCodesLoading && (
                  <div className="form-text"><span className="spinner-border spinner-border-sm me-1" />Loading lot codes…</div>
                )}
                {!lotCodesLoading && lotCodes.length > 0 && (
                  <div className="mt-2">
                    <div className="text-muted small mb-1">Available lot codes:</div>
                    <div className="d-flex flex-wrap gap-1">
                      {lotCodes.map(lc => (
                        <button
                          key={lc.lotCode}
                          type="button"
                          className={`btn btn-sm ${form.lotCode === lc.lotCode ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => set('lotCode', lc.lotCode)}
                          title={`Received: ${Number(lc.receivedKg).toFixed(3)} kg | Used: ${Number(lc.usedKg).toFixed(3)} kg`}
                        >
                          {lc.lotCode}
                          <span className="ms-1 badge bg-light text-dark fw-normal" style={{ fontSize: '0.7rem' }}>
                            {Number(lc.remainingKg).toFixed(1)} kg
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {!lotCodesLoading && form.ingredientId && lotCodes.length === 0 && (
                  <div className="form-text text-muted">No lot codes on record for this ingredient.</div>
                )}
              </div>

              <div className="row mb-3">
                <div className="col">
                  <label className="form-label">Adjustment Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={form.adjustmentType}
                    onChange={e => {
                      const type = e.target.value;
                      setForm(f => {
                        const qty = parseFloat(f.quantityKg);
                        const newQty = ALWAYS_REDUCTION_TYPES.has(type) && !isNaN(qty) && qty > 0
                          ? String(-qty)
                          : f.quantityKg;
                        return { ...f, adjustmentType: type, quantityKg: newQty };
                      });
                    }}
                  >
                    <option value="waste">Waste</option>
                    <option value="damage">Damage</option>
                    <option value="sample">Sample</option>
                    <option value="correction">Correction</option>
                    <option value="resale">Resale</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="col">
                  <label className="form-label">Quantity (kg) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    step="0.001"
                    className="form-control"
                    placeholder="e.g. -2.5 or +1.0"
                    value={form.quantityKg}
                    onChange={e => {
                      let val = e.target.value;
                      if (ALWAYS_REDUCTION_TYPES.has(form.adjustmentType)) {
                        const n = parseFloat(val);
                        if (!isNaN(n) && n > 0) val = String(-n);
                      }
                      set('quantityKg', val);
                    }}
                  />
                  <div className="form-text">
                    {ALWAYS_REDUCTION_TYPES.has(form.adjustmentType)
                      ? `${ADJ_TYPE_LABELS[form.adjustmentType]} always reduces stock — quantity will be negative.`
                      : 'Negative = reduction, positive = addition'}
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Reason <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.reason}
                  onChange={e => set('reason', e.target.value)}
                  placeholder="Explain why this adjustment is being made"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.notes}
                  onChange={e => set('notes', e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={onSave} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const CONDITION_LABELS = { always: 'Always', temperatureControlled: 'Temp-controlled only', frozen: 'Frozen only' };

function CheckTemplateConfigModal({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, showConfirm, , onResolveDialog] = useDialog();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ label: '', description: '', condition: 'always', enabled: true });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await ingredientInventoryService.getCheckTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load checks.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const openNew = () => {
    setEditingTemplate(null);
    setForm({ label: '', description: '', condition: 'always', enabled: true });
    setError('');
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    setForm({ label: t.label, description: t.description || '', condition: t.condition || 'always', enabled: t.enabled !== false });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) { setError('Label is required.'); return; }
    setError('');
    setSaving(true);
    try {
      if (editingTemplate) {
        await ingredientInventoryService.updateCheckTemplate(editingTemplate.id, {
          label: form.label.trim(),
          description: form.description.trim() || null,
          condition: form.condition,
          enabled: form.enabled,
        });
      } else {
        await ingredientInventoryService.createCheckTemplate({
          label: form.label.trim(),
          description: form.description.trim() || null,
          condition: form.condition,
          enabled: form.enabled,
        });
      }
      setShowForm(false);
      fetchTemplates();
    } catch {
      setError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    const ok = await showConfirm('Delete Check', `Delete "${t.label}"? This cannot be undone.`, { confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      await ingredientInventoryService.deleteCheckTemplate(t.id);
      fetchTemplates();
    } catch {
      setError('Delete failed.');
    }
  };

  const handleToggleEnabled = async (t) => {
    try {
      await ingredientInventoryService.updateCheckTemplate(t.id, { enabled: !t.enabled });
      fetchTemplates();
    } catch {
      setError('Update failed.');
    }
  };

  const handleSeed = async () => {
    const ok = await showConfirm('Load Default Checks', 'Load default acceptance checks? Existing checks will not be replaced.', { confirmLabel: 'Load Defaults', variant: 'primary' });
    if (!ok) return;
    try {
      await ingredientInventoryService.seedCheckTemplates();
      fetchTemplates();
    } catch {
      setError('Seed failed.');
    }
  };

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">⚙ Acceptance Check Configuration</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                These checks appear on every new receiving log. Toggle them on/off, edit labels, or add your own.
                Checks marked <span className="badge bg-secondary">Temp</span> or <span className="badge bg-info text-dark">Frozen</span> only appear when the respective option is enabled on the log.
              </p>

              {error && <div className="alert alert-danger py-2">{error}</div>}

              {loading ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : (
                <>
                  {templates.length === 0 ? (
                    <div className="alert alert-warning">
                      No checks configured yet.{' '}
                      <button className="btn btn-sm btn-outline-secondary ms-2" onClick={handleSeed}>Load Defaults</button>
                    </div>
                  ) : (
                    <ul className="list-group mb-3">
                      {templates.map(t => (
                        <li key={t.id} className={`list-group-item d-flex align-items-start gap-2 py-2${!t.enabled ? ' opacity-50' : ''}`}>
                          <div className="form-check form-switch mt-1 mb-0" style={{ minWidth: 44 }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={t.enabled !== false}
                              onChange={() => handleToggleEnabled(t)}
                              title={t.enabled !== false ? 'Disable' : 'Enable'}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-medium">{t.label}</div>
                            {t.description && <div className="text-muted small">{t.description}</div>}
                          </div>
                          {t.condition && t.condition !== 'always' && (
                            <span className={`badge align-self-center ${t.condition === 'frozen' ? 'bg-info text-dark' : 'bg-secondary'}`}>
                              {CONDITION_LABELS[t.condition]}
                            </span>
                          )}
                          <div className="d-flex gap-1 align-self-center">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(t)}>Edit</button>
                            {!t.isSystem && (
                              <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(t)}>Del</button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {showForm ? (
                    <div className="card">
                      <div className="card-body">
                        <h6 className="fw-semibold mb-3">{editingTemplate ? 'Edit Check' : 'New Check'}</h6>
                        <div className="mb-3">
                          <label className="form-label">Label <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            value={form.label}
                            onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                            placeholder="e.g. Packaging integrity verified"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Description <span className="text-muted small">(optional)</span></label>
                          <input
                            type="text"
                            className="form-control"
                            value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Brief instruction or clarification"
                          />
                        </div>
                        <div className="row g-3 mb-3">
                          <div className="col-md-6">
                            <label className="form-label">Condition</label>
                            <select
                              className="form-select"
                              value={form.condition}
                              onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}
                            >
                              <option value="always">Always shown</option>
                              <option value="temperatureControlled">Temperature-controlled items only</option>
                              <option value="frozen">Frozen items only</option>
                            </select>
                          </div>
                          <div className="col-md-6 d-flex align-items-end">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="chkEnabled"
                                checked={form.enabled}
                                onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                              />
                              <label className="form-check-label" htmlFor="chkEnabled">Enabled</label>
                            </div>
                          </div>
                        </div>
                        {error && <div className="alert alert-danger py-2 mb-2">{error}</div>}
                        <div className="d-flex gap-2">
                          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : editingTemplate ? 'Update' : 'Add Check'}
                          </button>
                          <button className="btn btn-outline-secondary" onClick={() => { setShowForm(false); setError(''); }}>Cancel</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex gap-2">
                      <button className="btn btn-outline-primary btn-sm" onClick={openNew}>+ Add Check</button>
                      {templates.length === 0 && (
                        <button className="btn btn-outline-secondary btn-sm" onClick={handleSeed}>Load Defaults</button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal dialog={dialog} onResolve={onResolveDialog} />
    </>
  );
}

function ReceivingRecordPrintModal({ log, companySettings, onClose }) {
  const printAreaRef = React.useRef();

  // Inject @media print CSS to show only the print area
  useEffect(() => {
    const style = document.createElement('style');
    style.dataset.receiptPrint = '1';
    style.textContent = `
      @media print {
        body * { visibility: hidden !important; }
        .receiving-record-print-area,
        .receiving-record-print-area * { visibility: visible !important; }
        .receiving-record-print-area {
          position: fixed !important;
          top: 0 !important; left: 0 !important;
          width: 100% !important;
          background: white !important;
          padding: 24px !important;
          z-index: 99999 !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, []);

  const fmtDate = (d) => d ? parseDateOnly(d).toLocaleDateString() : '—';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '—';

  const passLabel = (passed) => {
    if (passed === true) return { text: 'Pass', cls: 'text-success fw-semibold' };
    if (passed === false) return { text: 'Fail', cls: 'text-danger fw-semibold' };
    return { text: 'N/A', cls: 'text-muted' };
  };

  const companyName = companySettings?.name || '';
  const checks = (log.CheckResults || []).slice().sort((a, b) => (a.CheckTemplate?.sortOrder ?? 0) - (b.CheckTemplate?.sortOrder ?? 0));
  const lotCodes = log.LotCodes || [];
  const documents = log.Documents || [];

  return (
    <>
      {/* Print CSS injects via useEffect above */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header no-print">
              <h5 className="modal-title">Receiving Record #{log.id}</h5>
              <div className="d-flex gap-2 ms-auto me-2">
                <button className="btn btn-primary btn-sm" onClick={() => window.print()}>
                  Print / Save as PDF
                </button>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body p-4">
              {/* ── This div is what gets printed ── */}
              <div ref={printAreaRef} className="receiving-record-print-area">

                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom">
                  <div>
                    {companyName && <div className="fw-bold fs-5">{companyName}</div>}
                    <div className="text-muted small">
                      Generated: {new Date().toLocaleString()}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold fs-5">Ingredient Receiving Record</div>
                    <div className="text-muted small">Record # {log.id}</div>
                    {log.verifiedAt ? (
                      <span className="badge bg-success">VERIFIED</span>
                    ) : (
                      <span className="badge bg-warning text-dark">PENDING VERIFICATION</span>
                    )}
                  </div>
                </div>

                {/* Delivery Details */}
                <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  Delivery Information
                </h6>
                <div className="row g-2 mb-3 small">
                  <div className="col-6 col-md-4">
                    <span className="text-muted">Ingredient:</span><br />
                    <strong>{log.Ingredient?.name || '—'}</strong>
                  </div>
                  <div className="col-6 col-md-2">
                    <span className="text-muted">RM Code:</span><br />
                    <strong>{log.Ingredient?.rawMaterialCode || '—'}</strong>
                  </div>
                  <div className="col-6 col-md-3">
                    <span className="text-muted">Supplier:</span><br />
                    {log.sourceType === 'internal' ? (
                      <strong>🏭 Internal Production{log.producedByRunId ? ` (Prep CCP run #${log.producedByRunId})` : ''}</strong>
                    ) : (
                      <strong>{log.supplierName || '—'}</strong>
                    )}
                  </div>
                  <div className="col-6 col-md-3">
                    <span className="text-muted">Delivery Date:</span><br />
                    <strong>{fmtDate(log.deliveryDate)}</strong>
                  </div>
                  <div className="col-6 col-md-4">
                    <span className="text-muted">Total Quantity:</span><br />
                    <strong>{Number(log.totalQuantityKg || 0).toFixed(3)} kg</strong>
                  </div>
                  {log.unitCost != null && (
                    <div className="col-6 col-md-4">
                      <span className="text-muted">Unit Cost ($/kg):</span><br />
                      <strong>{Number(log.unitCost).toFixed(4)}</strong>
                    </div>
                  )}
                  <div className="col-6 col-md-4">
                    <span className="text-muted">Received By:</span><br />
                    <strong>{log.receivedByName || '—'}</strong>
                  </div>
                  <div className="col-6 col-md-4">
                    <span className="text-muted">Received At:</span><br />
                    <strong>{fmtDateTime(log.receivedAt)}</strong>
                  </div>
                </div>

                {/* Lot Codes */}
                {lotCodes.length > 0 && (
                  <>
                    <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Lot Codes
                    </h6>
                    <table className="table table-sm table-bordered small mb-3" style={{ maxWidth: 400 }}>
                      <thead className="table-light">
                        <tr>
                          <th>Lot Code</th>
                          <th className="text-end">Quantity (kg)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lotCodes.map((lc, i) => (
                          <tr key={i}>
                            <td>{lc.lotCode}</td>
                            <td className="text-end">{Number(lc.quantityKg || 0).toFixed(3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* Temperature / Cold Chain */}
                {log.isTemperatureControlled && (
                  <>
                    <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Temperature &amp; Cold Chain
                    </h6>
                    <div className="row g-2 mb-3 small">
                      <div className="col-4">
                        <span className="text-muted">Temp-controlled:</span><br />
                        <strong>Yes</strong>
                      </div>
                      <div className="col-4">
                        <span className="text-muted">Received Temperature:</span><br />
                        <strong>{log.receivedTemperature != null ? `${log.receivedTemperature}°C` : '—'}</strong>
                      </div>
                      {log.isFrozen && (
                        <>
                          <div className="col-4">
                            <span className="text-muted">Frozen:</span><br />
                            <strong>Yes</strong>
                          </div>
                          <div className="col-4">
                            <span className="text-muted">Arrived Frozen:</span><br />
                            <strong>{log.checkFrozenIsFrozen === true ? 'Yes' : log.checkFrozenIsFrozen === false ? 'No' : '—'}</strong>
                          </div>
                          <div className="col-4">
                            <span className="text-muted">No Signs of Thawing:</span><br />
                            <strong>{log.checkFrozenNoThawing === true ? 'Yes' : log.checkFrozenNoThawing === false ? 'No' : '—'}</strong>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {/* Acceptance Checks */}
                {checks.length > 0 && (
                  <>
                    <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Acceptance Checks
                    </h6>
                    <table className="table table-sm table-bordered small mb-3">
                      <thead className="table-light">
                        <tr>
                          <th>Check</th>
                          <th style={{ width: 80 }} className="text-center">Result</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checks.map((cr) => {
                          const { text, cls } = passLabel(cr.passed);
                          return (
                            <tr key={cr.id}>
                              <td>{cr.checkLabelSnapshot || cr.CheckTemplate?.label || '—'}</td>
                              <td className={`text-center ${cls}`}>{text}</td>
                              <td className="text-muted">{cr.notes || ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}

                {/* Rejection */}
                {log.hasRejection && (
                  <>
                    <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Rejection
                    </h6>
                    <div className="row g-2 mb-3 small">
                      {log.rejectedQuantityKg != null && (
                        <div className="col-6 col-md-3">
                          <span className="text-muted">Rejected Quantity:</span><br />
                          <strong className="text-danger">{Number(log.rejectedQuantityKg).toFixed(3)} kg</strong>
                        </div>
                      )}
                      {log.rejectionDescription && (
                        <div className="col-12">
                          <span className="text-muted">Description:</span><br />
                          <strong>{log.rejectionDescription}</strong>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Deviation / Corrective Action */}
                {(log.deviation || log.correctiveAction) && (
                  <>
                    <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Deviation / Corrective Action
                    </h6>
                    <div className="row g-2 mb-3 small">
                      {log.deviation && (
                        <div className="col-12 col-md-6">
                          <span className="text-muted">Deviation:</span><br />
                          <strong>{log.deviation}</strong>
                        </div>
                      )}
                      {log.correctiveAction && (
                        <div className="col-12 col-md-6">
                          <span className="text-muted">Corrective Action:</span><br />
                          <strong>{log.correctiveAction}</strong>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Notes */}
                {log.notes && (
                  <>
                    <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Notes
                    </h6>
                    <p className="small mb-3">{log.notes}</p>
                  </>
                )}

                {/* Documents */}
                {documents.length > 0 && (
                  <>
                    <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Attached Documents
                    </h6>
                    <ul className="list-unstyled small mb-3">
                      {documents.map(doc => (
                        <li key={doc.id}>
                          &#128206; {docTypeLabel(doc.documentType)}{doc.documentOriginalName ? ` — ${doc.documentOriginalName}` : ''}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* Verification */}
                <div className="border-top pt-3 mt-2">
                  <h6 className="fw-bold mb-2 text-uppercase text-secondary" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    Verification
                  </h6>
                  <div className="row g-2 small">
                    {log.verifiedAt ? (
                      <>
                        <div className="col-6 col-md-4">
                          <span className="text-muted">Verified By:</span><br />
                          <strong>{log.verifiedByName || '—'}</strong>
                        </div>
                        <div className="col-6 col-md-4">
                          <span className="text-muted">Verified At:</span><br />
                          <strong>{fmtDateTime(log.verifiedAt)}</strong>
                        </div>
                      </>
                    ) : (
                      <div className="col-12">
                        <span className="text-warning fw-semibold">Not yet verified</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Electronic signatures */}
                <div className="row mt-4 pt-3 border-top" style={{ pageBreakInside: 'avoid' }}>
                  <div className="col-5">
                    <div className="text-muted small mb-1" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Received By — Electronic Signature
                    </div>
                    {log.receivedByName ? (
                      <div
                        style={{
                          fontFamily: "'Dancing Script', 'Brush Script MT', cursive",
                          fontSize: '1.6rem',
                          color: '#1a1a6e',
                          lineHeight: 1.2,
                          borderBottom: '1px solid #999',
                          paddingBottom: 4,
                          marginBottom: 4,
                        }}
                      >
                        {log.receivedByName}
                      </div>
                    ) : (
                      <div style={{ borderBottom: '1px solid #999', height: 36, marginBottom: 4 }} />
                    )}
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                      {log.receivedByName || '—'} &nbsp;|&nbsp; {fmtDateTime(log.receivedAt)}
                    </div>
                  </div>
                  <div className="col-2" />
                  <div className="col-5">
                    <div className="text-muted small mb-1" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Verified By — Electronic Signature
                    </div>
                    {log.verifiedByName ? (
                      <div
                        style={{
                          fontFamily: "'Dancing Script', 'Brush Script MT', cursive",
                          fontSize: '1.6rem',
                          color: '#1a1a6e',
                          lineHeight: 1.2,
                          borderBottom: '1px solid #999',
                          paddingBottom: 4,
                          marginBottom: 4,
                        }}
                      >
                        {log.verifiedByName}
                      </div>
                    ) : (
                      <div style={{ borderBottom: '1px solid #999', height: 36, marginBottom: 4 }} />
                    )}
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                      {log.verifiedByName ? (
                        <>{log.verifiedByName} &nbsp;|&nbsp; {fmtDateTime(log.verifiedAt)}</>
                      ) : (
                        'Not yet verified'
                      )}
                    </div>
                  </div>
                </div>

              </div>{/* end print area */}
            </div>
            <div className="modal-footer no-print">
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              <button className="btn btn-primary" onClick={() => window.print()}>
                Print / Save as PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PriceHistoryPromptModal({ prompt, onClose }) {
  const [effectiveDate, setEffectiveDate] = useState(prompt.deliveryDate || today());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await ingredientService.addPrice(prompt.ingredientId, {
        pricePerKg: prompt.pricePerKg,
        effectiveDate,
        note: note.trim() || null,
      });
      onClose();
    } catch {
      setError('Failed to save price. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Record Price in History?</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="mb-3 text-muted small">
              A unit cost was recorded for this delivery. Would you like to also save it to the
              price history for <strong>{prompt.ingredientName}</strong>?
            </p>
            <div className="mb-3">
              <label className="form-label small fw-semibold">Price per kg</label>
              <input
                className="form-control"
                value={Number(prompt.pricePerKg).toFixed(4)}
                readOnly
              />
            </div>
            <div className="mb-3">
              <label className="form-label small fw-semibold">Effective Date</label>
              <input
                type="date"
                className="form-control"
                value={effectiveDate}
                onChange={e => setEffectiveDate(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label small fw-semibold">Note <span className="text-muted fw-normal">(optional)</span></label>
              <input
                type="text"
                className="form-control"
                placeholder='e.g. "new supplier", "seasonal price increase"'
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </div>
            {error && <div className="alert alert-danger py-2 small">{error}</div>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Skip
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !effectiveDate}>
              {saving ? 'Saving…' : 'Record Price'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IngredientInventoryPage;
